import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Debug: log environment variables
console.log('Supabase URL:', supabaseUrl)
console.log('Supabase Key exists:', !!supabaseAnonKey)

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Helper function to get current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) {
    console.error('Error getting user:', error)
    return null
  }
  return user
}

// Helper function to sign in with Google
export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin
    }
  })
  return { data, error }
}

// Helper function to sign out
export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  return { error }
}

// Conversation management service
export const conversationService = {
  // Get all conversations for current user
  async getConversations() {
    const { data: conversations, error } = await supabase
      .from('conversations')
      .select(`
        id,
        title,
        created_at,
        updated_at,
        messages (
          id,
          role,
          content,
          sources,
          created_at
        )
      `)
      .order('updated_at', { ascending: false })

    if (error) {
      console.error('Error fetching conversations:', error)
      throw error
    }

    return conversations || []
  },

  // Create a new conversation
  async createConversation(title) {
    const { data: user } = await supabase.auth.getUser()
    if (!user?.user) throw new Error('Not authenticated')

    const { data, error } = await supabase
      .from('conversations')
      .insert([
        {
          user_id: user.user.id,
          title: title,
        }
      ])
      .select()
      .single()

    if (error) {
      console.error('Error creating conversation:', error)
      throw error
    }

    return data
  },

  // Save a message to a conversation
  async saveMessage(conversationId, role, content, sources = []) {
    const { data, error } = await supabase
      .from('messages')
      .insert([
        {
          conversation_id: conversationId,
          role: role,
          content: content,
          sources: sources
        }
      ])
      .select()
      .single()

    if (error) {
      console.error('Error saving message:', error)
      throw error
    }

    // Update conversation's updated_at timestamp
    await supabase
      .from('conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', conversationId)

    return data
  },

  // Get messages for a specific conversation
  async getMessages(conversationId) {
    const { data: messages, error } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Error fetching messages:', error)
      throw error
    }

    return messages || []
  },

  // Delete a conversation and all its messages
  async deleteConversation(conversationId) {
    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId)

    if (error) {
      console.error('Error deleting conversation:', error)
      throw error
    }
  },

  // Update conversation title
  async updateConversationTitle(conversationId, newTitle) {
    const { data, error } = await supabase
      .from('conversations')
      .update({ title: newTitle })
      .eq('id', conversationId)
      .select()
      .single()

    if (error) {
      console.error('Error updating conversation title:', error)
      throw error
    }

    return data
  }
}
